import {Component, inject, Input} from '@angular/core';
import {NoWidgetDataComponent} from '../../../../components/my-brand-screen/no-widget-data/no-widget-data.component';
import {HighchartsChartModule} from 'highcharts-angular';
import * as Highcharts from 'highcharts';
import {CommonModule} from '@angular/common';
import {ChartData, WidgetValueField} from '../../../types/common';
import {Format, UtilService} from '../../../../services/util.service';
import {LoaderComponent} from '../../loader/loader.component';

const POINT_SPACING = 12;
const POINT_PADDING = 2;
const COMPARISON_COLOR = '#0A5C5B';
const WIDGET_HEIGHT = 260;

interface InternalAxis extends Highcharts.Axis {
  len: number;
  left: number;
  min: number;
  max: number;
}

interface CustomChart extends Highcharts.Chart {
  custom?: {
    comparisonDot?: Highcharts.SVGElement;
  };
}

@Component({
  selector: 'app-horizontal-bar-chart',
  imports: [CommonModule, HighchartsChartModule, NoWidgetDataComponent, LoaderComponent],
  templateUrl: './horizontal-bar-chart.component.html',
  styleUrl: './horizontal-bar-chart.component.scss'
})
export class HorizontalBarChartComponent {
  @Input() data: ChartData[] = [];
  @Input() colors: string[] = [];
  @Input() format: Format = '';
  @Input() valueField: WidgetValueField = '';
  @Input() comparison: Partial<ChartData> = {};
  @Input() comparisonTitle?: string;
  @Input() loading = false;
  @Input() noDataView?: string;
  Highcharts: typeof Highcharts = Highcharts;
  chartOptions!: Highcharts.Options;
  private axisFontStyle = {
    fontFamily: 'Poppins',
    fontSize: '12px',
    fontStyle: 'normal',
    fontWeight: '400',
    color: '#4E5258'
  }
  private _chartInstance?: CustomChart;
  utilService = inject(UtilService);


  set chartInstance(instance: Highcharts.Chart | undefined) {
    if (instance) {
      this._chartInstance = instance;
      const value = this.comparison[this.valueField];
      this.renderComparisonDot(instance, value as number, COMPARISON_COLOR);
    }
  }

  ngOnChanges() {
    if (this.data.length) {
      const maxValue = Math.max(...[...this.data, this.comparison].map(d => d[this.valueField] ?? 0));
      const labelText = this.comparison?.name || this.comparisonTitle || '';
      this.chartOptions = {
        chart: {
          type: 'bar',
          spacingBottom: 38,
          marginTop: 62,
          marginRight: 15,
          events: {
            redraw: () => {
              const value = this.comparison[this.valueField];
              this.updateComparisonDot(this._chartInstance as CustomChart, value as number);
            },
            render: function () {
              const yAxis = this.yAxis[0];
              const ticks = yAxis.ticks;
              const lastTick = ticks[100];
              if (lastTick && lastTick.label) {
                lastTick.label.attr({x: +lastTick.label.attr('x') + 10});
              }
            }
          }
        },
        title: {
          text: undefined,
        },
        tooltip: {
          enabled: false
        },
        legend: {
          enabled: false,
        },
        credits: {
          enabled: false,
        },
        xAxis:
          {
            type: 'category',
            title: {text: null},
            lineWidth: 0,
            tickLength: 0,
            labels: {
              style: this.axisFontStyle
            },
          },
        yAxis: {
          min: 0,
          max: this.format === 'shortPercentage' ? 100 : maxValue * 1.2,
          tickInterval: this.format === 'shortPercentage' ? 20 : undefined,
          tickAmount: 6,
          title: {
            text: undefined
          },
          gridLineWidth: 0.5,
          labels: {
            enabled: true,
            formatter: (function (utilService, format) {
              return function () {
                return utilService.formatWidgetValue(this.value as number, format) as string;
              };
            })(this.utilService, this.format),
            style: this.axisFontStyle
          },
          plotLines: [
            {
              value: this.comparison?.[this.valueField],
              color: COMPARISON_COLOR,
              dashStyle: 'ShortDash',
              width: 2,
              label: {
                text: this.comparisonTitle,
                align: 'center',
                verticalAlign: 'top',
                y: -28,
                x: 0,
                style: {
                  boxShadow: '0px 0px 5px 0px rgba(0, 0, 0, 0.15)',
                  zIndex: 9,
                  background: '#FFFFFF',
                  padding: '2px 4px',
                  borderRadius: 4
                },
                rotation: 0,
                useHTML: true,
                formatter: function () {
                  return `<div style="
                    font-family: Poppins, sans-serif;
                    font-size: 12px;
                    font-weight: 400;
                    color: #4E5258;
                    text-align: center;
                    white-space: nowrap;
                    display: inline-block;
                  ">${labelText}</div>`;
                }
              },
              zIndex: 9
            }
          ]
        },
        plotOptions: this.getPlotOptions(),
        colors: this.colors,
        series: [
          {
            type: 'bar',
            name: this.valueField,
            data: this.data.map(item => ({
              name: item.name,
              y: item[this.valueField]
            })),
            colorByPoint: true,
            groupPadding: 0,
            pointWidth: this.calculatePointWidth()
          }
        ]
      };
    }
  }

  private getPlotOptions(): Highcharts.PlotOptions {
    return {
      bar: {
        borderRadius: 4,
        dataLabels: {
          enabled: false,
        },
        pointWidth: undefined,
      },
      series: {
        states: {
          hover: {
            enabled: true,
            brightness: 0.1
          },
          inactive: {
            opacity: 0.2
          }
        },
        point: {
          events: {
            mouseOver: function () {
              this.series.points.forEach(point => {
                if (point !== this) {
                  point.setState('inactive');
                }
              });
            },
            mouseOut: function () {
              this.series.points.forEach(point => {
                point.setState('');
              });
            },
          }
        }
      }
    }
  }

  private calculatePointWidth(): number {
    const numberOfPoints = this.data.length;
    if (numberOfPoints < 2) {
      return 42;
    }
    return Math.floor((WIDGET_HEIGHT - ((numberOfPoints) * POINT_SPACING)) / numberOfPoints) + POINT_PADDING;
  }

  private calculateDotPosition(chart: Highcharts.Chart, value: number) {
    const yAxis = chart.yAxis[0] as InternalAxis;
    const axisMin = yAxis.min;
    const axisMax = yAxis.max;
    const axisLen = yAxis.len;
    const plotLeft = yAxis.left;
    const relativePosition = (value - axisMin) / (axisMax - axisMin);
    const x = plotLeft + axisLen * relativePosition;
    const y = chart.plotTop - 7;

    return {x, y};
  }

  private renderComparisonDot(chart: CustomChart, value: number, color: string) {
    const {x, y} = this.calculateDotPosition(chart, value);
    chart.custom = chart.custom || {};
    chart.custom.comparisonDot = chart.renderer
      .circle(x, y, 5)
      .attr({
        fill: color,
        zIndex: 10
      })
      .add();
  }

  private updateComparisonDot(chart: CustomChart, value: number) {
    const dot = chart.custom?.comparisonDot;
    if (!dot) return;

    const {x, y} = this.calculateDotPosition(chart, value);
    dot.attr({cx: x, cy: y});
  }
}
